import { AuthenticatedPhotoDirective } from '@shared/components/entity-photos/authenticated-photo.directive';
import { Component, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  switchMap,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  startWith,
  map,
  catchError,
  of,
  tap,
  shareReplay,
  merge,
} from 'rxjs';
import { MembershipStatus, MinistryStatus, PersonAgeStatus } from '@core/api/generated/api-client';
import { personAgeStatusLabels, personAgeStatusOptions } from '@core/api/enums/person-age-status.labels';
import { membershipStatusLabels, membershipStatusOptions } from '@core/api/enums/membership-status.labels';
import { ministryStatusLabels, ministryStatusOptions } from '@core/api/enums/ministry-status.labels';
import { FilterChip, PaginationConfig, TableColumn } from '@shared/components/data-table/data-table.component';
import { ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import {
  DataTableMultiSelectFilterComponent,
  DataTableMultiSelectFilterGroup,
  DataTableMultiSelectFilterOption,
} from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { MembershipStatusBadgeComponent } from '@shared/components/membership-status-badge/membership-status-badge.component';
import { UnitService } from '../../services/unit.service';
import { OrganizationUnit, MemberRow } from '../../models/unit.model';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { formatPhoneNumberForDisplay } from '@shared/utils/phone-number-format.util';
import { PhoneNumberPipe } from '@shared/pipes/phone-number.pipe';
import { MemberReferenceComponent } from '@shared/components/member-reference/member-reference.component';
import { ResponsiblePriestDialogComponent } from '@features/people/components/responsible-priest-dialog.component';
import { PersonService, ResponsiblePriestCandidate } from '@features/people/services/person.service';
import { PersonStatusDialogComponent, PersonStatusDialogResult } from '@features/people/components/person-status-dialog.component';
import { DeleteConfirmDialogComponent, DeleteConfirmDialogData } from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { NotificationService } from '@shared/services/notification.service';

@Component({
  selector: 'app-unit-members-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuthenticatedPhotoDirective,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatMenuModule,
    MatTooltipModule,
    TableSurfaceComponent,
    DataTableMultiSelectFilterComponent,
    MembershipStatusBadgeComponent,
    PhoneNumberPipe,
    MemberReferenceComponent,
  ],
  templateUrl: './unit-members-page.component.html',
  styleUrl: './unit-members-page.component.scss',
})
export class UnitMembersPageComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly ANY_MINISTRY_FILTER = '__any__';
  private readonly EMPTY_MINISTRY_FILTER = '__empty__';
  private readonly ANY_COMMUNITY_FILTER = '__any__';
  private readonly EMPTY_COMMUNITY_FILTER = '__empty__';
  private readonly UNDETERMINED_AGE_FILTER = '__undetermined__';

  unitId = '';
  readonly failedMemberPhotos = new Set<string>();
  unit: OrganizationUnit | null = null;
  unitLoading = true;
  membersLoadErrorMessage: string | null = null;

  readonly filterControl = new FormControl('');
  readonly filterForm = new FormGroup({
    status: new FormControl<MembershipStatus[]>(['Active'], { nonNullable: true }),
    ageStatus: new FormControl<string[]>([], { nonNullable: true }),
    ministry: new FormControl<string[]>([], { nonNullable: true }),
    ministryStatus: new FormControl<MinistryStatus[]>(
      ministryStatusOptions.map(option => option.value),
      { nonNullable: true },
    ),
    community: new FormControl<string[]>([], { nonNullable: true }),
    responsiblePriest: new FormControl<string[]>([], { nonNullable: true }),
  });

  columns: TableColumn[] = [
    { key: 'firstName', header: 'Nombre', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'lastName', header: 'Apellido', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'status', header: 'Estado', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'phone', header: 'Telefono', hideable: true, visible: true, resizable: true },
    { key: 'age', header: 'Edad', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'ministry', header: 'Ministerio', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'responsiblePriest', header: 'Pastor de hogar', hideable: true, visible: true, resizable: true },
    { key: 'actions', header: '', width: '96px', minWidth: '96px', hideable: false, visible: true, resizable: false, exportable: false },
  ];

  tableData: MemberRow[] = [];
  isTableLoading = true;
  allMembers: MemberRow[] = [];
  filteredMembers: MemberRow[] = [];
  activeFilterChips: FilterChip[] = [];
  readonly ageStatusOptions: DataTableMultiSelectFilterOption[] = [
    { value: this.UNDETERMINED_AGE_FILTER, label: 'Sin determinar' },
    ...personAgeStatusOptions,
  ];
  readonly ageStatusGroups: DataTableMultiSelectFilterGroup[] = [
    { label: 'Sin determinar', values: [this.UNDETERMINED_AGE_FILTER] },
    { label: 'Bebé', values: ['Baby'] },
    { label: 'Pre-escuela', values: ['PED1', 'PED2'] },
    { label: 'Escuela Dominical', values: ['ED1', 'ED2', 'ED3', 'ED4'] },
    { label: 'Escuela de Religión', values: ['ER1', 'ER2', 'ER3', 'AC'] },
    { label: 'Juventud', values: ['Youth'] },
    { label: 'Adulto/a', values: ['Adult'] },
    { label: '3ra Edad', values: ['Senior'] },
  ];
  readonly membershipStatusOptions = membershipStatusOptions;
  readonly ministryStatusOptions = ministryStatusOptions;
  ministryFilterOptions: string[] = [];
  communityFilterOptions: string[] = [];
  responsiblePriestFilterOptions: ResponsiblePriestCandidate[] = [];
  ministrySelectOptions: DataTableMultiSelectFilterOption[] = [
    { value: this.EMPTY_MINISTRY_FILTER, label: 'Sin ministerio' },
  ];
  communitySelectOptions: DataTableMultiSelectFilterOption[] = [
    { value: this.ANY_COMMUNITY_FILTER, label: 'Con comunidad' },
    { value: this.EMPTY_COMMUNITY_FILTER, label: 'Sin comunidad' },
  ];
  responsiblePriestSelectOptions: DataTableMultiSelectFilterOption[] = [
    { value: this.EMPTY_MINISTRY_FILTER, label: 'Sin pastor de hogar' },
  ];
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  canManageMemberData = false;
  isAdmin = false;
  currentUserPersonId?: string;

  private readonly sort$ = new BehaviorSubject<{ sortBy: string; direction: 'asc' | 'desc' }>(
    { sortBy: 'lastName', direction: 'asc' }
  );
  private readonly membersReload$ = new Subject<void>();
  private ageStatusFilterInitialized = false;
  private ministryFilterInitialized = false;
  private communityFilterInitialized = false;
  private responsiblePriestFilterInitialized = false;
  private selectMinistriesOnlyFromQuery = false;

  get currentSort(): ToolbarSortSelection {
    return this.sort$.value;
  }

  get showCommunityFilter(): boolean {
    return this.columns.some(column => column.key === 'community');
  }

  get paginationConfig(): PaginationConfig {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalCount: this.totalCount,
      totalPages: this.totalPages,
    };
  }

  onSortChange(event: { sortBy: string; direction: 'asc' | 'desc' }): void {
    this.currentPage = 1;
    this.sort$.next(event);
  }

  onToolbarSort(event: ToolbarSortSelection): void {
    this.currentPage = 1;
    this.sort$.next(event);
  }

  onToolbarSearch(term: string): void {
    this.currentPage = 1;
    this.filterControl.setValue(term);
  }

  onFilterRemove(key: string): void {
    this.currentPage = 1;
    switch (key) {
      case 'status':
        this.selectAllMembershipStatuses();
        return;
      case 'ageStatus':
        this.selectAllAgeStatuses();
        return;
      case 'ministry':
        this.selectAllMinistries();
        return;
      case 'ministryStatus':
        this.selectAllMinistryStatuses();
        return;
      case 'community':
        this.selectAllCommunities();
        return;
      case 'responsiblePriest':
        this.selectAllResponsiblePriests();
        return;
    }
  }

  createMember(): void {
    if (!this.canManageMemberData) {
      return;
    }

    this.router.navigate(['/people/new'], { queryParams: { unitId: this.unitId } });
  }

  onPageChange(event: { page: number }): void {
    this.currentPage = event.page;
    this.applyMembersPage();
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.applyMembersPage();
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly unitService: UnitService,
    private readonly cdr: ChangeDetectorRef,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly userFullInfoService: UserFullInfoService,
    private readonly dialog: MatDialog,
    private readonly personService: PersonService,
    private readonly notifications: NotificationService,
  ) {
    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.canManageMemberData = this.userFullInfoService.canManageMemberDataSnapshot(user);
        this.isAdmin = user?.isAdmin === true;
        this.currentUserPersonId = user?.personId;
        this.cdr.markForCheck();
      });

    combineLatest(this.route.pathFromRoot.map(route => route.paramMap)).pipe(
      map(paramMaps => paramMaps.map(params => params.get('unit-id')).find((unitId): unitId is string => !!unitId)),
      takeUntil(this.destroy$),
    ).subscribe(unitId => {
      if (!unitId) {
        this.unitLoading = false;
        this.isTableLoading = false;
        this.membersLoadErrorMessage = 'No se pudo identificar la unidad.';
        this.cdr.markForCheck();
        return;
      }

      this.unitId = unitId;
      this.applyQueryFilters();
      this.personService.getResponsiblePriestCandidatesByUnit(this.unitId)
        .pipe(catchError(() => of([] as ResponsiblePriestCandidate[])), takeUntil(this.destroy$))
        .subscribe(candidates => {
          this.responsiblePriestFilterOptions = candidates;
          this.updateResponsiblePriestSelectOptions();
          this.initializeResponsiblePriestFilter();
          this.cdr.markForCheck();
        });

      // Load unit info for header
      this.unitService.getUnit(this.unitId).pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      ).subscribe(unit => {
        this.unit = unit;
        if (unit) {
          this.breadcrumbService.setDynamicCrumb('unit-id', unit.name);
        }
        this.unitLoading = false;
        if (unit && unit.organizationLevelId !== 5) {
          this.columns = [
            { key: 'firstName', header: 'Nombre', sortable: true, hideable: true, visible: true, resizable: true },
            { key: 'lastName', header: 'Apellido', sortable: true, hideable: true, visible: true, resizable: true },
            { key: 'status', header: 'Estado', sortable: true, hideable: true, visible: true, resizable: true },
            { key: 'community', header: 'Comunidad', sortable: true, hideable: true, visible: true, resizable: true },
            { key: 'ministry', header: 'Ministerio', sortable: true, hideable: true, visible: true, resizable: true },
            { key: 'age', header: 'Edad', sortable: true, hideable: true, visible: true, resizable: true },
            { key: 'phone', header: 'Telefono', hideable: true, visible: true, resizable: true },
            { key: 'responsiblePriest', header: 'Pastor de hogar', hideable: true, visible: true, resizable: true },
            { key: 'actions', header: '', width: '96px', minWidth: '96px', hideable: false, visible: true, resizable: false, exportable: false },
          ];
        }
        this.cdr.markForCheck();
      });

      // Load members
      const members$ = merge(
        of(void 0),
        this.membersReload$,
        this.filterForm.controls.status.valueChanges.pipe(
          distinctUntilChanged(),
          map(() => void 0),
        ),
      ).pipe(
        tap(() => {
          this.isTableLoading = true;
          this.membersLoadErrorMessage = null;
          this.tableData = [];
          this.cdr.markForCheck();
        }),
        switchMap(() => this.unitService.getMembersFull(
          this.unitId,
          this.filterForm.controls.status.value.includes('Departed'),
        ).pipe(
          catchError(error => {
            this.membersLoadErrorMessage = this.getMembersLoadErrorMessage(error);
            return of([] as MemberRow[]);
          })
        )),
        shareReplay(1),
      );

      const filter$ = this.filterControl.valueChanges.pipe(
        startWith(''),
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => {
          this.currentPage = 1;
        }),
        map(v => (v ?? '').trim().toLowerCase()),
      );

      const structuredFilters$ = this.filterForm.valueChanges.pipe(
        startWith(this.filterForm.getRawValue()),
        tap(() => {
          this.currentPage = 1;
          this.updateActiveFilterChips();
          this.cdr.markForCheck();
        }),
      );

      combineLatest([members$, filter$, structuredFilters$, this.sort$]).pipe(
        tap(([members]) => {
          this.allMembers = members;
          this.updateFilterOptions();
          this.updateActiveFilterChips();
          this.isTableLoading = false;
        }),
        map(([members, filterValue, _structuredFilters, sort]) => {
          const structuredFilters = this.filterForm.getRawValue();
          let result = filterValue
            ? members.filter(m =>
                m.firstName.toLowerCase().includes(filterValue) ||
                m.lastName.toLowerCase().includes(filterValue) ||
                (m.phone ?? '').toLowerCase().includes(filterValue) ||
                (formatPhoneNumberForDisplay(m.phone) ?? '').toLowerCase().includes(filterValue) ||
                (m.communityName ?? '').toLowerCase().includes(filterValue) ||
                (m.ministryName ?? '').toLowerCase().includes(filterValue),
              )
            : members;

          const selectedAgeStatuses = structuredFilters.ageStatus ?? [];
          if (selectedAgeStatuses.length === 0) {
            result = [];
          } else if (!this.areAllSelected(selectedAgeStatuses, this.ageStatusFilterValues)) {
            result = result.filter(member => {
              const ageStatus = this.toAgeStatusFilterOption(member.ageStatus);
              return ageStatus === null
                ? selectedAgeStatuses.includes(this.UNDETERMINED_AGE_FILTER)
                : selectedAgeStatuses.includes(ageStatus.value);
            });
          }

          const selectedMembershipStatuses = structuredFilters.status ?? [];
          if (selectedMembershipStatuses.length === 0) {
            result = [];
          } else {
            result = result.filter(member => {
              const membershipStatus = this.normalizeMembershipStatus(member.status);
              return membershipStatus !== undefined && selectedMembershipStatuses.includes(membershipStatus);
            });
          }

          const selectedMinistries = structuredFilters.ministry ?? [];
          if (selectedMinistries.length === 0) {
            result = [];
          } else if (!this.areAllSelected(selectedMinistries, this.ministryFilterValues)) {
            result = result.filter(member => this.matchesMinistryFilter(member, selectedMinistries));
          }

          const selectedMinistryStatuses = structuredFilters.ministryStatus ?? [];
          if (selectedMinistryStatuses.length === 0) {
            result = [];
          } else if (!this.areAllSelected(selectedMinistryStatuses, this.ministryStatusFilterValues)) {
            result = result.filter(member =>
              member.ministryStatus !== undefined &&
              selectedMinistryStatuses.includes(member.ministryStatus) &&
              this.hasMinistryInCurrentUnit(member)
            );
          }

          const selectedCommunities = structuredFilters.community ?? [];
          if (selectedCommunities.length === 0) {
            result = [];
          } else if (!this.areAllSelected(selectedCommunities, this.communityFilterValues)) {
            result = result.filter(member => this.matchesCommunityFilter(member, selectedCommunities));
          }

          const selectedResponsiblePriests = structuredFilters.responsiblePriest ?? [];
          if (selectedResponsiblePriests.length === 0) {
            result = [];
          } else if (!this.areAllSelected(selectedResponsiblePriests, this.responsiblePriestFilterValues)) {
            result = result.filter(member => this.matchesResponsiblePriestFilter(member, selectedResponsiblePriests));
          }

          return [...result].sort((a, b) => {
            let valA: string | number | undefined;
            let valB: string | number | undefined;
            switch (sort.sortBy) {
              case 'firstName':  valA = a.firstName.toLowerCase(); valB = b.firstName.toLowerCase(); break;
              case 'lastName':   valA = a.lastName.toLowerCase();  valB = b.lastName.toLowerCase();  break;
              case 'status':     valA = a.status;                  valB = b.status;                  break;
              case 'age':        valA = a.age;                     valB = b.age;                     break;
              case 'community':  valA = a.communityName?.toLowerCase(); valB = b.communityName?.toLowerCase(); break;
              case 'ministry':   valA = this.ministrySortOrderForCurrentUnit(a); valB = this.ministrySortOrderForCurrentUnit(b); break;
            }

            const aHasValue = valA !== undefined && valA !== '';
            const bHasValue = valB !== undefined && valB !== '';
            if (aHasValue !== bHasValue) return aHasValue ? -1 : 1;
            if (!aHasValue) return 0;
            if (valA === undefined || valA === '' || valB === undefined || valB === '') return 0;

            if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
            return 0;
          });
        }),
        takeUntil(this.destroy$),
      ).subscribe(filtered => {
        this.filteredMembers = filtered;
        this.applyMembersPage();
        this.cdr.markForCheck();
      });
    });
  }

  openMemberProfile(member: MemberRow, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/units', this.unitId, 'members', member.id]);
  }

  phoneLink(phone?: string): string | null {
    const digits = phone?.replace(/\D/g, '');
    return digits ? `tel:${phone?.trim().startsWith('+') ? '+' : ''}${digits}` : null;
  }

  goBack(): void {
    this.breadcrumbService.back();
  }

  retryMembers(): void {
    this.membersReload$.next();
  }

  manageResponsiblePriest(member: MemberRow, event: Event): void {
    event.stopPropagation();
    if (!this.canManageMemberData) return;
    this.dialog.open(ResponsiblePriestDialogComponent, { data: { personId: member.id, personName: `${member.firstName} ${member.lastName}`, responsiblePriestId: member.responsiblePriestId } })
      .afterClosed().subscribe(changed => { if (changed) this.membersReload$.next(); });
  }

  navigateToMemberEdit(member: MemberRow, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/units', this.unitId, 'members', member.id, 'edit']);
  }

  navigateToMemberEvents(member: MemberRow, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/units', this.unitId, 'members', member.id, 'events']);
  }

  navigateToMemberCommunityHistory(member: MemberRow, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/units', this.unitId, 'members', member.id, 'community-history']);
  }

  changeMemberStatus(member: MemberRow, event: Event): void {
    event.stopPropagation();
    this.dialog.open(PersonStatusDialogComponent, modalDialogConfig({
      data: { status: member.status },
      size: 'sm',
      layout: 'form',
    })).afterClosed().subscribe((result: PersonStatusDialogResult | null) => {
      if (!result) {
        return;
      }

      this.personService.updateStatus(member.id, result).subscribe({
        next: () => {
          this.notifications.success('Estado actualizado correctamente.');
          this.membersReload$.next();
        },
        error: () => this.notifications.error('No se pudo actualizar el estado.'),
      });
    });
  }

  createUserForMember(member: MemberRow, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/users/new'], { queryParams: { personId: member.id } });
  }

  deleteMember(member: MemberRow, event: Event): void {
    event.stopPropagation();
    if (member.id === this.currentUserPersonId) {
      this.notifications.error('No podés eliminar tu propia persona.');
      return;
    }

    this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar persona',
        message: `¿Estás seguro de que querés eliminar <strong>${member.firstName} ${member.lastName}</strong>? Esta acción no se puede deshacer.`,
        confirmButtonText: 'Eliminar',
        requiresReason: true,
      } as DeleteConfirmDialogData,
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    })).afterClosed().subscribe(result => {
      if (result?.confirmed !== true) {
        return;
      }

      this.personService.deletePerson(member.id, result.deletionReason).subscribe({
        next: () => {
          this.notifications.success(`La persona "${member.firstName} ${member.lastName}" fue eliminada correctamente.`);
          this.membersReload$.next();
        },
        error: () => this.notifications.error('No se pudo eliminar la persona.'),
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleHomeAttendance(member: MemberRow, event: Event): void {
    event.stopPropagation();
    this.personService.updateHomeAttendance(member.id, !member.isHomeAttended)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          member.isHomeAttended = !member.isHomeAttended;
          this.membersReload$.next();
          this.notifications.success(
            member.isHomeAttended
              ? 'El miembro fue marcado como atendido en el hogar.'
              : 'El miembro dejó de estar marcado como atendido en el hogar.',
          );
          this.cdr.markForCheck();
        },
        error: () => this.notifications.error('No se pudo actualizar la atención en el hogar.'),
      });
  }

  private getMembersLoadErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && error.status === 408) {
      return 'La consulta tardó demasiado. Probá refrescar la lista.';
    }

    return 'No se pudo cargar la lista de miembros. Probá refrescar.';
  }

  private applyQueryFilters(): void {
    const ministry = this.route.snapshot.queryParamMap.get('ministry');

    if (ministry === this.ANY_MINISTRY_FILTER) {
      this.selectMinistriesOnlyFromQuery = true;
      this.ministryFilterInitialized = true;
    }
  }

  private uniqueValues<T extends string>(values: T[]): T[] {
    return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }

  private updateFilterOptions(): void {
    this.ministryFilterOptions = this.uniqueValues(
      this.allMembers.map(member => member.ministryName).filter(Boolean) as string[]
    );
    this.communityFilterOptions = this.uniqueValues(
      this.allMembers.map(member => member.communityName).filter(Boolean) as string[]
    );
    this.updateMinistrySelectOptions();
    this.updateCommunitySelectOptions();

    this.initializeAgeStatusFilter();
    this.initializeMinistryFilter();
    this.initializeCommunityFilter();
  }

  private updateActiveFilterChips(): void {
    const filters = this.filterForm.getRawValue();
    const chips: FilterChip[] = [];

    this.addMultiSelectFilterChip(chips, 'ageStatus', 'Edad', filters.ageStatus, this.ageStatusFilterValues, value => this.getAgeStatusFilterLabel(value));
    this.addMultiSelectFilterChip(chips, 'ministry', 'Ministerio', filters.ministry, this.ministryFilterValues, value => this.getMinistryFilterLabel(value));
    this.addMultiSelectFilterChip(chips, 'ministryStatus', 'Estado del ministerio', filters.ministryStatus, this.ministryStatusFilterValues, value => this.getMinistryStatusLabel(value));
    this.addMultiSelectFilterChip(chips, 'community', 'Comunidad', filters.community, this.communityFilterValues, value => this.getCommunityFilterLabel(value));

    if (filters.status.length === 0) {
      chips.push({ key: 'status', label: 'Estado: Ninguno', removable: true });
    } else if (!this.areAllMembershipStatusesSelected) {
      chips.push({
        key: 'status',
        label: `Estado: ${filters.status.map(status => this.getMembershipStatusLabel(status)).join(', ')}`,
        removable: true,
      });
    }
    this.addMultiSelectFilterChip(chips, 'responsiblePriest', 'Pastor de hogar', filters.responsiblePriest, this.responsiblePriestFilterValues, value => this.getResponsiblePriestFilterLabel(value));

    this.activeFilterChips = chips;
  }

  private applyMembersPage(): void {
    this.totalCount = this.filteredMembers.length;
    this.totalPages = Math.ceil(this.totalCount / this.pageSize);

    if (this.totalPages > 0 && this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.tableData = this.filteredMembers.slice(start, end);
    this.cdr.markForCheck();
  }

  private toAgeStatusFilterOption(status?: PersonAgeStatus | string | null): { value: string; label: string } | null {
    if (!status) {
      return null;
    }

    const label = personAgeStatusLabels[status as PersonAgeStatus] ?? status;
    return {
      value: String(status),
      label,
    };
  }

  private getAgeStatusFilterLabel(value: string): string {
    return this.ageStatusOptions.find(option => option.value === value)?.label ?? value;
  }

  get ageStatusFilterValues(): string[] {
    return this.ageStatusOptions.map(option => option.value);
  }

  get ministryFilterValues(): string[] {
    return [this.EMPTY_MINISTRY_FILTER, ...this.ministryFilterOptions];
  }

  get ministryStatusFilterValues(): MinistryStatus[] {
    return this.ministryStatusOptions.map(option => option.value);
  }

  get communityFilterValues(): string[] {
    return [this.ANY_COMMUNITY_FILTER, this.EMPTY_COMMUNITY_FILTER, ...this.communityFilterOptions];
  }

  get responsiblePriestFilterValues(): string[] {
    return [this.EMPTY_MINISTRY_FILTER, ...this.responsiblePriestFilterOptions.map(candidate => candidate.id)];
  }

  selectAllAgeStatuses(emitEvent = true): void {
    this.updateMultiSelectControl(this.filterForm.controls.ageStatus, this.ageStatusFilterValues, emitEvent);
  }

  selectAllMinistries(emitEvent = true): void {
    this.updateMultiSelectControl(this.filterForm.controls.ministry, this.ministryFilterValues, emitEvent);
  }

  selectAllMinistryStatuses(): void {
    this.updateMultiSelectControl(this.filterForm.controls.ministryStatus, this.ministryStatusFilterValues);
  }

  selectAllCommunities(emitEvent = true): void {
    this.updateMultiSelectControl(this.filterForm.controls.community, this.communityFilterValues, emitEvent);
  }

  selectAllResponsiblePriests(emitEvent = true): void {
    this.updateMultiSelectControl(this.filterForm.controls.responsiblePriest, this.responsiblePriestFilterValues, emitEvent);
  }

  get areAllMembershipStatusesSelected(): boolean {
    return this.areAllSelected(
      this.filterForm.controls.status.value,
      this.membershipStatusOptions.map(option => option.value),
    );
  }

  selectAllMembershipStatuses(): void {
    this.filterForm.controls.status.setValue(this.membershipStatusOptions.map(option => option.value));
  }

  private getMinistryFilterLabel(value: string): string {
    if (value === this.EMPTY_MINISTRY_FILTER) {
      return 'Sin ministerio';
    }

    return value;
  }

  private getCommunityFilterLabel(value: string): string {
    if (value === this.ANY_COMMUNITY_FILTER) {
      return 'Con comunidad';
    }

    if (value === this.EMPTY_COMMUNITY_FILTER) {
      return 'Sin comunidad';
    }

    return value;
  }

  getAgeStatusLabel(status: PersonAgeStatus): string {
    return personAgeStatusLabels[status] ?? status;
  }

  private getMembershipStatusLabel(status: MembershipStatus): string {
    return membershipStatusLabels[status] ?? status;
  }

  getMinistryStatusLabel(status: MinistryStatus): string {
    return ministryStatusLabels[status] ?? status;
  }

  hasActiveMinistryInCurrentUnit(member: MemberRow): boolean {
    return member.ministryStatus === 'Active' &&
      this.hasMinistryInCurrentUnit(member);
  }

  hasActiveMinistryInOtherUnits(member: MemberRow): boolean {
    return member.ministryStatus === 'Active' &&
      (member.activeMinistryUnitIds ?? []).some(unitId => unitId !== this.unitId);
  }

  private hasMinistryInCurrentUnit(member: MemberRow): boolean {
    return (member.activeMinistryUnitIds ?? []).includes(this.unitId);
  }

  private ministrySortOrderForCurrentUnit(member: MemberRow): number | undefined {
    return this.hasActiveMinistryInCurrentUnit(member)
      ? member.ministrySortOrder
      : undefined;
  }

  getResponsiblePriestFilterLabel(value: string): string {
    if (value === this.EMPTY_MINISTRY_FILTER) return 'Sin asignar';
    return this.responsiblePriestFilterOptions.find(candidate => candidate.id === value)?.fullName
      ?? this.allMembers.find(member => member.responsiblePriestId === value)?.responsiblePriestName
      ?? value;
  }

  private initializeAgeStatusFilter(): void {
    if (!this.ageStatusFilterInitialized) {
      this.ageStatusFilterInitialized = true;
      this.selectAllAgeStatuses(false);
    }
  }

  private initializeMinistryFilter(): void {
    if (this.selectMinistriesOnlyFromQuery) {
      this.selectMinistriesOnlyFromQuery = false;
      this.filterForm.controls.ministry.setValue([...this.ministryFilterOptions], { emitEvent: false });
      return;
    }

    if (!this.ministryFilterInitialized) {
      this.ministryFilterInitialized = true;
      this.selectAllMinistries(false);
    }
  }

  private initializeCommunityFilter(): void {
    if (!this.communityFilterInitialized) {
      this.communityFilterInitialized = true;
      this.selectAllCommunities(false);
    }
  }

  private initializeResponsiblePriestFilter(): void {
    if (!this.responsiblePriestFilterInitialized) {
      this.responsiblePriestFilterInitialized = true;
      this.selectAllResponsiblePriests();
    }
  }

  private updateMinistrySelectOptions(): void {
    this.ministrySelectOptions = [
      { value: this.EMPTY_MINISTRY_FILTER, label: 'Sin ministerio' },
      ...this.ministryFilterOptions.map(ministry => ({ value: ministry, label: ministry })),
    ];
  }

  private updateCommunitySelectOptions(): void {
    this.communitySelectOptions = [
      { value: this.ANY_COMMUNITY_FILTER, label: 'Con comunidad' },
      { value: this.EMPTY_COMMUNITY_FILTER, label: 'Sin comunidad' },
      ...this.communityFilterOptions.map(community => ({ value: community, label: community })),
    ];
  }

  private updateResponsiblePriestSelectOptions(): void {
    this.responsiblePriestSelectOptions = [
      { value: this.EMPTY_MINISTRY_FILTER, label: 'Sin pastor de hogar' },
      ...this.responsiblePriestFilterOptions.map(pastor => ({
        value: pastor.id,
        label: `${pastor.fullName} · ${pastor.ministryName}`,
      })),
    ];
  }

  private addMultiSelectFilterChip<T extends string>(
    chips: FilterChip[],
    key: string,
    label: string,
    selectedValues: T[],
    allValues: T[],
    getValueLabel: (value: T) => string,
  ): void {
    if (selectedValues.length === 0) {
      chips.push({ key, label: `${label}: Ninguno`, removable: true });
    } else if (!this.areAllSelected(selectedValues, allValues)) {
      chips.push({
        key,
        label: `${label}: ${selectedValues.map(getValueLabel).join(', ')}`,
        removable: true,
      });
    }
  }

  private areAllSelected<T extends string>(selectedValues: T[], allValues: T[]): boolean {
    return allValues.every(value => selectedValues.includes(value));
  }

  private updateMultiSelectControl<T extends string>(
    control: FormControl<T[]>,
    values: T[],
    emitEvent = true,
  ): void {
    control.setValue([...values], { emitEvent });
  }

  private matchesMinistryFilter(member: MemberRow, selectedValues: string[]): boolean {
    return (selectedValues.includes(this.EMPTY_MINISTRY_FILTER) && !member.ministryName) ||
      (!!member.ministryName && selectedValues.includes(member.ministryName));
  }

  private matchesCommunityFilter(member: MemberRow, selectedValues: string[]): boolean {
    return (selectedValues.includes(this.ANY_COMMUNITY_FILTER) && !!member.communityName) ||
      (selectedValues.includes(this.EMPTY_COMMUNITY_FILTER) && !member.communityName) ||
      (!!member.communityName && selectedValues.includes(member.communityName));
  }

  private matchesResponsiblePriestFilter(member: MemberRow, selectedValues: string[]): boolean {
    return (selectedValues.includes(this.EMPTY_MINISTRY_FILTER) && !member.responsiblePriestId) ||
      (!!member.responsiblePriestId && selectedValues.includes(member.responsiblePriestId));
  }

  private normalizeMembershipStatus(status?: MembershipStatus): MembershipStatus | undefined {
    if (typeof status === 'string') {
      return status;
    }

    switch (status as unknown) {
      case 1: return 'Active';
      case 2: return 'Inactive';
      case 3: return 'Left';
      case 4: return 'Departed';
      case 5: return 'Excluded';
      default: return undefined;
    }
  }

}
